import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { getSignupTrips, getStaff, getStudents, createSignupPick, updateSignupPick, deleteSignupPick, archiveSignupOption, unarchiveSignupOption } from "../../lib/api";
import OptionEditorDialog from "./OptionEditorDialog";
import { prettyDate } from "./signupHelpers";
import { sortByFirst } from "../../lib/names";

const responseColor = {
  yes: "bg-green-100 text-green-800 border-green-200",
  no: "bg-red-100 text-red-800 border-red-200",
  maybe: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function TripsTab() {
  const [trips, setTrips] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [studentSearch, setStudentSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    try {
      const [tr, st, sf] = await Promise.all([getSignupTrips(showArchived ? "archived" : "open"), getStudents("current", true), getStaff({ assignable: true })]);
      setTrips(tr.data);
      setStudents((st.data || []).filter((s) => s.status === "current"));
      setStaff([...(sf.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch {
      toast.error("Failed to load trips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const handleArchive = async (trip) => {
    if (!window.confirm(`Archive "${trip.title}"? Roster is preserved; you can unarchive later.`)) return;
    try {
      await archiveSignupOption(trip.id);
      toast.success("Archived");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleUnarchive = async (trip) => {
    try {
      await unarchiveSignupOption(trip.id);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setResponse = async (trip, student, response) => {
    const existing = (trip.picks || []).find((p) => p.student_id === student.id);
    try {
      if (existing) {
        if (response === "remove") {
          await deleteSignupPick(existing.id);
        } else if (existing.response !== response) {
          await updateSignupPick(existing.id, { response });
        }
      } else if (response !== "remove") {
        try {
          await createSignupPick({ option_id: trip.id, student_id: student.id, response });
        } catch (e) {
          if (e.response?.status === 409) {
            const proceed = window.confirm(e.response?.data?.detail + "\n\nAdd anyway?");
            if (!proceed) return;
            await createSignupPick({ option_id: trip.id, student_id: student.id, response, override_capacity: true });
          } else throw e;
        }
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handlePrint = (trip) => {
    const yes = (trip.picks || []).filter((p) => p.response === "yes");
    const maybe = (trip.picks || []).filter((p) => p.response === "maybe");
    const no = (trip.picks || []).filter((p) => p.response === "no");
    const rows = (list) =>
      list.map((p, i) => `<tr><td style="border:1px solid #ddd;padding:6px;width:30px;text-align:center">${i + 1}</td><td style="border:1px solid #ddd;padding:6px">${p.student_name}</td><td style="border:1px solid #ddd;padding:6px;font-size:11px;color:#666">${p.notes || ""}</td></tr>`).join("");
    const datesLabel = (trip.dates || []).map(prettyDate).join(", ");
    const blocksLabel = (trip.covered_blocks && trip.covered_blocks.length)
      ? trip.covered_blocks.map((k) => { const m = (k || "").match(/(\d+)/); return m ? `Block ${m[1]}` : k; }).join(", ")
      : "All blocks";
    const pw = window.open("", "_blank", "width=900,height=700");
    pw.document.write(`<html><head><title>${trip.title} Roster</title></head><body style="font-family:Arial;padding:24px">
      <h1 style="margin:0 0 8px">${trip.title}</h1>
      <div style="color:#555;margin-bottom:8px">${datesLabel}${trip.location ? ` · ${trip.location}` : ""}${trip.lead_staff_name ? ` · Lead: ${trip.lead_staff_name}` : ""} · Blocks: ${blocksLabel}</div>
      <h3>YES (${yes.length})</h3><table style="border-collapse:collapse;width:100%;margin-bottom:16px"><thead><tr style="background:#f0f0f0"><th style="border:1px solid #ddd;padding:6px">#</th><th style="border:1px solid #ddd;padding:6px;text-align:left">Student</th><th style="border:1px solid #ddd;padding:6px;text-align:left">Notes</th></tr></thead><tbody>${rows(yes) || '<tr><td colspan="3" style="border:1px solid #ddd;padding:6px;color:#999">—</td></tr>'}</tbody></table>
      <h3>MAYBE (${maybe.length})</h3><table style="border-collapse:collapse;width:100%;margin-bottom:16px"><thead><tr style="background:#f0f0f0"><th style="border:1px solid #ddd;padding:6px">#</th><th style="border:1px solid #ddd;padding:6px;text-align:left">Student</th><th style="border:1px solid #ddd;padding:6px;text-align:left">Notes</th></tr></thead><tbody>${rows(maybe) || '<tr><td colspan="3" style="border:1px solid #ddd;padding:6px;color:#999">—</td></tr>'}</tbody></table>
      <h3>NO (${no.length})</h3><table style="border-collapse:collapse;width:100%"><thead><tr style="background:#f0f0f0"><th style="border:1px solid #ddd;padding:6px">#</th><th style="border:1px solid #ddd;padding:6px;text-align:left">Student</th><th style="border:1px solid #ddd;padding:6px;text-align:left">Notes</th></tr></thead><tbody>${rows(no) || '<tr><td colspan="3" style="border:1px solid #ddd;padding:6px;color:#999">—</td></tr>'}</tbody></table>
      <script>window.onload=()=>window.print()</script>
    </body></html>`);
    pw.document.close();
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00305E]" /></div>;

  const filteredStudents = (q) => {
    const base = q.trim()
      ? students.filter((s) => `${s.preferred_name || s.first_name} ${s.last_name}`.toLowerCase().includes(q.toLowerCase()))
      : students;
    return sortByFirst(base);
  };

  // Show trips/events in date order (soonest first); undated go last.
  const sortedTrips = [...trips].sort((a, b) => {
    const da = (a.dates || []).slice().sort()[0] || "9999-99-99";
    const db = (b.dates || []).slice().sort()[0] || "9999-99-99";
    return da.localeCompare(db);
  });

  return (
    <div className="space-y-4" data-testid="trips-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold">Trips & Events</h3>
          <p className="text-sm text-muted-foreground">One-off interest sign-ups. Students answer Yes / No / Maybe. Past events auto-archive after 3 days.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex">
            <Button variant={!showArchived ? "default" : "outline"} size="sm" onClick={() => setShowArchived(false)} className="rounded-r-none" data-testid="trips-show-open">Active</Button>
            <Button variant={showArchived ? "default" : "outline"} size="sm" onClick={() => setShowArchived(true)} className="rounded-l-none" data-testid="trips-show-archived">Archived</Button>
          </div>
          {!showArchived && <Button onClick={() => { setEditing(null); setEditorOpen(true); }} className="bg-[#00305E]" data-testid="add-trip-btn">+ Add Trip / Event</Button>}
        </div>
      </div>

      {trips.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">
          {showArchived ? "No archived trips yet." : "No trips yet. Add one to start collecting interest."}
        </CardContent></Card>
      ) : sortedTrips.map((t) => {
        const isOpen = expandedId === t.id;
        return (
          <Card key={t.id} data-testid={`trip-${t.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="space-y-1">
                  <CardTitle className="text-base">{t.title}</CardTitle>
                  <CardDescription className="space-x-2">
                    <span>{(t.dates || []).map(prettyDate).join(", ")}</span>
                    {t.location && <span>· {t.location}</span>}
                    {t.lead_staff_name && <span>· Lead: {t.lead_staff_name}</span>}
                    <span>· Blocks: {(t.covered_blocks && t.covered_blocks.length) ? t.covered_blocks.map((k) => { const m = (k || "").match(/(\d+)/); return m ? `Block ${m[1]}` : k; }).join(", ") : "All blocks"}</span>
                  </CardDescription>
                  <div className="flex gap-2">
                    <Badge className="bg-green-100 text-green-800">{t.yes_count || 0} Yes</Badge>
                    <Badge className="bg-amber-100 text-amber-800">{t.maybe_count || 0} Maybe</Badge>
                    <Badge className="bg-red-100 text-red-800">{t.no_count || 0} No</Badge>
                    {t.capacity != null && <Badge variant="outline">Cap: {t.capacity}</Badge>}
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => handlePrint(t)} data-testid={`trip-print-${t.id}`}>Print Roster</Button>
                  {!showArchived ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setEditorOpen(true); }}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-amber-700" onClick={() => handleArchive(t)} data-testid={`trip-archive-${t.id}`}>Archive</Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="sm" className="text-green-700" onClick={() => handleUnarchive(t)} data-testid={`trip-unarchive-${t.id}`}>Restore</Button>
                  )}
                  <Button size="sm" onClick={() => setExpandedId(isOpen ? null : t.id)} data-testid={`trip-toggle-${t.id}`}>{isOpen ? "Close" : "Manage"}</Button>
                </div>
              </div>
              {t.description && <p className="text-sm text-gray-700 pt-2">{t.description}</p>}
            </CardHeader>
            {isOpen && (
              <CardContent className="space-y-3">
                <input
                  className="w-full text-sm border rounded px-2 py-1"
                  placeholder="Search students..."
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                />
                <div className="max-h-96 overflow-y-auto border rounded">
                  {filteredStudents(studentSearch).map((s) => {
                    const pick = (t.picks || []).find((p) => p.student_id === s.id);
                    return (
                      <div key={s.id} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 hover:bg-gray-50" data-testid={`trip-${t.id}-student-${s.id}`}>
                        <span className="text-sm">{(s.preferred_name || s.first_name)} {s.last_name}</span>
                        <div className="flex gap-1">
                          {["yes", "maybe", "no"].map((r) => (
                            <Button
                              key={r}
                              size="sm"
                              variant={pick?.response === r ? "default" : "outline"}
                              onClick={() => setResponse(t, s, pick?.response === r ? "remove" : r)}
                              className={`h-7 px-2 text-xs ${pick?.response === r ? responseColor[r] : ""}`}
                              data-testid={`trip-${t.id}-pick-${s.id}-${r}`}
                            >
                              {r.toUpperCase()}
                            </Button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      <OptionEditorDialog
        open={editorOpen} onOpenChange={setEditorOpen}
        editing={editing} defaultType="trip" allowTypeSwitch={false}
        slots={[]} staff={staff} weekDates={[]}
        onSaved={() => { setEditorOpen(false); load(); }}
      />
    </div>
  );
}
